/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { unlockATicketForUser } from 'api/generalTicket'
import { WrapperRow } from 'components/auditorium/auditorium.style'
import { ActionButton, TransparentButton } from 'components/button/button.styled'
import { EventCardListing } from 'components/eventCard/eventCardListing'
import { ButtonContainer } from 'components/form/form.style'
import { PlaceholderContainer } from 'components/placeholder'
import {
    Container,
    HeaderRow,
    PageTitle,
    PriceText,
    RemoveAllButton,
    SeatContainer,
    SeatLabel,
    SubSectionContainer,
    TicketInfo,
    TicketPrice,
    TicketPriceText,
} from 'components/ticket/ticket.style'
import { useGlobalSettings } from 'hooks/admin/useGlobalSettings'
import { useToken } from 'hooks/token'
import { useSeatLocking } from 'hooks/user/useSelling'
import { useTotalPriceTickets } from 'hooks/user/useSelling/useTotalPriceTickets'
import { useReservationTimer } from 'hooks/user/useTickets/useReservationTimer'
import { useTicketsLockedByUser } from 'hooks/user/useTickets/useTicketsLockedByUser'
import { Row } from 'primereact/row'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EventDetails, SeatSellingModes, Ticket } from 'types/Event'
import { getSeatLabelForSeatId } from 'utils/seatLabel'

interface Props {
    currentEvent: EventDetails
}

export const Cart = ({ currentEvent }: Props) => {
    const navigate = useNavigate()
    const { sessionId } = useToken()
    const seatLocking = useSeatLocking(currentEvent.event_id, sessionId ?? '')

    const {
        data: ticketsLockedByUser,
        isLoading: ticketsLockedByUserIsLoading,
        error: ticketsLockedByUserError,
        refetch: ticketsLockedByUserRefetch,
    } = useTicketsLockedByUser(currentEvent.event_id, sessionId ?? '')
    const { formatted: reservationTime } = useReservationTimer(ticketsLockedByUser)

    const ticketIds = useMemo(() => {
        if (!ticketsLockedByUser) return []
        return ticketsLockedByUser.map((t: Ticket) => t.ticket_id)
    }, [ticketsLockedByUser])

    const seatIds = useMemo(() => {
        if (!ticketsLockedByUser) return []
        return ticketsLockedByUser.map((t: Ticket) => t.seat_id)
    }, [ticketsLockedByUser])

    const [isLoadingPayment, setIsLoadingPayment] = useState(false)
    const [paymentError, setPaymentError] = useState<string | null>(null)

    const totalPrice = useTotalPriceTickets(ticketsLockedByUser)

    const seatMap = useMemo(() => {
        const map = new Map<string, any>()
        currentEvent.location.auditorium.seats.forEach((seat) => {
            seat.seat_id && map.set(seat.seat_id, seat)
        })

        currentEvent.location.auditorium.gallery?.seats.forEach((seat) => {
            seat.seat_id && map.set(seat.seat_id, seat)
        })

        return map
    }, [currentEvent.location.auditorium.seats, currentEvent.location.auditorium.gallery?.seats])

    const removeSeat = async (seatId: string) => {
        await seatLocking?.unlockTicketHandler(seatId)
        window.location.reload()
    }

    const removeAllTickets = async () => {
        await unlockATicketForUser(currentEvent.event_id, sessionId ?? '', ticketsLockedByUser?.length)
        window.location.reload()
    }

    const navigateToCustomerData = () => {
        navigate(`/jegyek/vasarlo/${currentEvent.event_id}?seats=${seatIds.join(',')}`)
    }

    const isAnySeatSelected = ticketIds.length > 0 && ticketIds[0] !== ''

    const { systemFee, totalSystemFee, grandTotal } = useGlobalSettings(ticketIds.length, totalPrice)

    return (
        <Container>
            {currentEvent && isAnySeatSelected ? (
                <>
                    <PageTitle>Kosár</PageTitle>
                    {currentEvent.event_id && <EventCardListing eventId={currentEvent.event_id} showPurchaseButton={false} />}
                    {currentEvent.seat_selling_mode === SeatSellingModes.SeatBased && (
                        <SubSectionContainer>
                            {reservationTime && (
                                <Row>
                                    <strong style={{ color: '#D32F2F' }}>{reservationTime}</strong>
                                </Row>
                            )}
                            {ticketsLockedByUser?.map((ticket) => {
                                const seat = seatMap.get(ticket.seat_id)
                                if (!seat) {
                                    return <li key={ticket.seat_id}>Érvénytelen ülőhely: {ticket.seat_id}</li>
                                }

                                const seatLabel = getSeatLabelForSeatId(currentEvent, ticket.seat_id) ?? 'Ismeretlen hely'
                                const price = ticket.price ? `${ticket.price} Ft` : 'N/A'

                                return (
                                    <SeatContainer key={ticket.seat_id}>
                                        <SeatLabel>{seatLabel}</SeatLabel>
                                        <PriceText>{price}</PriceText>
                                        <ButtonContainer>
                                            <TransparentButton
                                                icon="pi pi-times"
                                                onClick={() => {
                                                    if (ticket) {
                                                        removeSeat(ticket.seat_id)
                                                    }
                                                }}
                                            />
                                        </ButtonContainer>
                                    </SeatContainer>
                                )
                            })}
                        </SubSectionContainer>
                    )}

                    {currentEvent.seat_selling_mode === SeatSellingModes.General && isAnySeatSelected && (
                        <SubSectionContainer>
                            <HeaderRow>
                                <TicketInfo>
                                    {reservationTime && (
                                        <Row>
                                            <strong style={{ color: '#D32F2F' }}>{reservationTime}</strong>
                                        </Row>
                                    )}
                                    {ticketIds.length} jegy kiválasztva
                                    {ticketIds.length > 0 &&
                                        (() => {
                                            const ticketPrices = ticketsLockedByUser?.map((ticket) => ticket.price)

                                            const uniquePrices = Array.from(new Set(ticketPrices))

                                            return uniquePrices.length === 1 ? ` – ${uniquePrices[0]} Ft/jegy` : ` – változó ár`
                                        })()}
                                </TicketInfo>

                                <RemoveAllButton onClick={removeAllTickets}>Összes eltávolítása</RemoveAllButton>
                            </HeaderRow>
                        </SubSectionContainer>
                    )}

                    <SubSectionContainer style={{ marginTop: '1rem', padding: '12px 16px' }}>
                        <WrapperRow>
                            <TicketPriceText>Jegyek összesen:</TicketPriceText>
                            <TicketPrice>{totalPrice} Ft</TicketPrice>
                        </WrapperRow>
                        <WrapperRow>
                            <TicketPriceText>+ Rendszerhasználati díj:</TicketPriceText>
                            <TicketPrice>
                                {ticketIds.length} x {systemFee} Ft = {totalSystemFee} Ft
                            </TicketPrice>
                        </WrapperRow>
                        <hr style={{ border: '0.5px solid #ddd', width: '100%' }} />
                        <WrapperRow>
                            <TicketPriceText>Összesen fizetendő:</TicketPriceText>
                            <TicketPrice>{grandTotal} Ft</TicketPrice>
                        </WrapperRow>
                        <WrapperRow>
                            <ButtonContainer>
                                <ActionButton
                                    label={isLoadingPayment ? 'Fizetés folyamatban...' : 'Vásárlás véglegesítése'}
                                    onClick={navigateToCustomerData}
                                    disabled={isLoadingPayment}
                                />
                            </ButtonContainer>
                        </WrapperRow>
                        {paymentError && <p style={{ color: 'red' }}>{paymentError}</p>}
                    </SubSectionContainer>
                </>
            ) : (
                <PlaceholderContainer>
                    {!currentEvent ? 'Ez az esemény sajnos nem elérhető. Kérjük térjen vissza a főoldalra.' : 'A kosár jelenleg üres.'}
                </PlaceholderContainer>
            )}
        </Container>
    )
}
